/**
 * NotificationsBell — small icon button that lives at the right-most
 * edge of the Instrument Rack, between the Quota segment and the rack
 * border. Click opens the NotificationDrawer.
 *
 * Hidden when the notification feed has zero entries — keeps the rack
 * tight when nothing's happened yet. The unseen-count badge surfaces
 * when entries exist; the icon itself is the discoverable affordance.
 */
import { useServerNotifications } from "@features/notifications";
import NotificationsIcon from "@mui/icons-material/NotificationsNoneRounded";
import { Badge, IconButton, Tooltip } from "@mui/material";
import { useNotifications, useUnseenCount } from "@stores/notificationStore";

export function NotificationsBell() {
  const clientTotal = useNotifications((s) => s.notifications.length);
  const clientUnseen = useUnseenCount();
  const openDrawer = useNotifications((s) => s.openDrawer);

  // Server-persisted billing/account alerts contribute to the badge too.
  const { data: serverFeed } = useServerNotifications();
  const total = clientTotal + (serverFeed?.items.length ?? 0);
  const unseen = clientUnseen + (serverFeed?.unread_count ?? 0);

  // Render even when total === 0 so the affordance is discoverable —
  // the empty drawer's BlueprintEmptyState is brand-aligned and reads
  // intentional. Power users learn the icon's there before the first
  // notification fires.
  return (
    <Tooltip title="Activity feed">
      <IconButton
        size="small"
        onClick={openDrawer}
        sx={(t) => ({
          mr: 1,
          alignSelf: "center",
          color:
            total === 0 ? t.palette.text.disabled : t.palette.text.secondary,
          "&:hover": { color: t.palette.accent.main },
        })}
      >
        <Badge
          color="error"
          variant="dot"
          invisible={unseen === 0}
          overlap="circular"
        >
          <NotificationsIcon sx={{ fontSize: 16 }} />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
